import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Camera, DollarSign, TrendingUp, Tag, Truck, CheckCircle } from 'lucide-react';

const SellerGuide = () => {
  const { themeColors } = useTheme();

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '20px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const sectionStyle = {
    marginBottom: '60px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px'
  };

  const cardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  };

  const paragraphStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '1.8',
    marginBottom: '16px'
  };

  const listStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '2',
    paddingLeft: '20px',
    marginBottom: '16px'
  };

  const stepStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    borderLeft: `4px solid ${themeColors.primary}`,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)'
  };

  const stepTitleStyle = {
    color: themeColors.text,
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '12px'
  };

  const iconTextStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: themeColors.text,
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '16px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '16px'
  };

  const tableHeaderStyle = {
    backgroundColor: themeColors.primary + '15',
    color: themeColors.text,
    padding: '12px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: `2px solid ${themeColors.border}`
  };

  const tableCellStyle = {
    padding: '12px',
    borderBottom: `1px solid ${themeColors.border}`,
    color: themeColors.textSecondary
  };

  const exampleBoxStyle = {
    backgroundColor: themeColors.primary + '05',
    border: `1px solid ${themeColors.border}`,
    borderRadius: '8px',
    padding: '20px',
    marginTop: '16px'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Seller Guide</h1>
          <p style={subtitleStyle}>
            Everything you need to know to sell successfully on BabyResell. From your first listing to getting paid, we've got you covered.
          </p>
        </div>

        {/* Getting Started */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Getting Started as a Seller</h2>

          <div style={stepStyle}>
            <div style={stepTitleStyle}>Step 1: Set Up Your Profile</div>
            <p style={paragraphStyle}>
              First impressions matter! Complete your profile with a friendly photo and bio. Buyers are more likely to purchase from sellers with complete profiles. Mention that you're a parent — it builds trust in our community.
            </p>
          </div>

          <div style={stepStyle}>
            <div style={stepTitleStyle}>Step 2: Complete Seller Onboarding</div>
            <p style={paragraphStyle}>
              To receive payouts, you'll need to connect your bank account through our secure Stripe integration. Go to your Seller Dashboard and follow the onboarding steps. This is a one-time setup.
            </p>
          </div>

          <div style={stepStyle}>
            <div style={stepTitleStyle}>Step 3: Create Your First Listing</div>
            <p style={paragraphStyle}>
              Click 'Create Listing' in the navigation bar. You'll need: photos (up to 8), a title, description, category, condition rating, age group, and your asking price.
            </p>
          </div>
        </div>

        {/* Photography Tips */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Photography Tips</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <Camera size={24} color={themeColors.primary} />
              Great photos are the single biggest factor in selling quickly
            </div>
            <p style={{ ...paragraphStyle, fontWeight: '500', marginBottom: '20px' }}>
              Here's how to take photos that sell:
            </p>
            <ul style={listStyle}>
              <li><strong>Use natural light</strong> — Photograph near a window during the day. Avoid harsh flash.</li>
              <li><strong>Clean background</strong> — Use a white sheet, clean floor, or plain wall. Clutter distracts buyers.</li>
              <li><strong>Multiple angles</strong> — Show the front, back, sides, and any important details or flaws.</li>
              <li><strong>Show scale</strong> — Place a common object nearby so buyers understand the size.</li>
              <li><strong>Be honest about flaws</strong> — Photograph any stains, scratches, or wear. Honesty builds trust and prevents disputes.</li>
              <li><strong>First photo matters most</strong> — Your primary image is what appears in the browse grid. Make it count.</li>
            </ul>
          </div>
        </div>

        {/* Pricing Strategy */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Pricing Strategy</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <DollarSign size={24} color={themeColors.primary} />
              Pricing Guide by Condition
            </div>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Condition</th>
                  <th style={tableHeaderStyle}>Suggested Price Range (% of retail)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tableCellStyle}><strong>New with tags</strong></td>
                  <td style={tableCellStyle}>70-85% of retail</td>
                </tr>
                <tr>
                  <td style={tableCellStyle}><strong>Like New</strong></td>
                  <td style={tableCellStyle}>50-70% of retail</td>
                </tr>
                <tr>
                  <td style={tableCellStyle}><strong>Good</strong></td>
                  <td style={tableCellStyle}>30-50% of retail</td>
                </tr>
                <tr>
                  <td style={tableCellStyle}><strong>Fair</strong></td>
                  <td style={tableCellStyle}>15-30% of retail</td>
                </tr>
              </tbody>
            </table>
            <p style={{ ...paragraphStyle, fontWeight: '600', marginTop: '20px', marginBottom: '12px' }}>
              Additional tips:
            </p>
            <ul style={listStyle}>
              <li>Search BabyResell for similar items to see what others are charging</li>
              <li>Premium brands (UPPAbaby, Bugaboo, Pottery Barn Kids) hold their value better</li>
              <li>Bundle related items together (e.g., "newborn clothing lot") for better value</li>
              <li>Factor in your shipping costs when setting prices</li>
              <li>Consider seasonal demand — winter clothes sell better in fall, swimwear in spring</li>
            </ul>
          </div>
        </div>

        {/* Writing Great Descriptions */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Writing Great Descriptions</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <Tag size={24} color={themeColors.primary} />
              A good description answers every question a buyer might have
            </div>
            <ul style={listStyle}>
              <li>What is the item? (Brand, model, size, color)</li>
              <li>What condition is it in? (Be specific and honest)</li>
              <li>Why are you selling it? (Outgrown, upgraded, unused gift)</li>
              <li>What's included? (Accessories, original packaging, manuals)</li>
              <li>Any defects or wear? (Always disclose — buyers appreciate honesty)</li>
              <li>Dimensions or sizing details?</li>
              <li>From a smoke-free / pet-free home? (Buyers care about this)</li>
            </ul>

            <div style={exampleBoxStyle}>
              <p style={{ color: themeColors.text, fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>
                Example of a great listing:
              </p>
              <p style={{ color: themeColors.text, fontWeight: 'bold', marginBottom: '8px' }}>
                BabyBjörn Carrier Mini — Cotton, Dusty Pink — Like New
              </p>
              <p style={{ ...paragraphStyle, marginBottom: 0, fontSize: '15px' }}>
                Used for about 2 months before our baby outgrew it. No stains, tears, or wear. All buckles and straps work perfectly. Comes with original box and instruction manual. From a smoke-free, pet-free home. Retails for $99 — selling for $55.
              </p>
            </div>
          </div>
        </div>

        {/* Shipping Best Practices */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Shipping Best Practices</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <Truck size={24} color={themeColors.primary} />
              Shipping Tips
            </div>
            <ul style={listStyle}>
              <li>USPS Priority Mail is usually the best value for baby items — includes tracking and $50 insurance</li>
              <li>Ship within 3 business days of the sale for the best buyer experience</li>
              <li>Pack items securely — use bubble wrap or packing paper, especially for fragile items</li>
              <li>Add tracking immediately — buyers get nervous without tracking info</li>
              <li>Consider offering free shipping — build the cost into your item price. "Free shipping" listings get more views</li>
              <li>Save boxes and packing materials — reuse shipping supplies to keep costs down</li>
            </ul>
          </div>
        </div>

        {/* After the Sale */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>After the Sale</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <CheckCircle size={24} color={themeColors.success} />
              What Happens Next
            </div>
            <ol style={{ ...listStyle, paddingLeft: '40px' }}>
              <li>Ship the item promptly and add the tracking number</li>
              <li>Message the buyer to let them know it's on the way</li>
              <li>Once delivered, the buyer has 48 hours to confirm receipt or open a dispute</li>
              <li>After confirmation (or auto-release), your payout is processed</li>
              <li>Encourage the buyer to leave a review — great reviews lead to more sales</li>
            </ol>
          </div>
        </div>

        {/* Growing Your Shop */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Growing Your Shop</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <TrendingUp size={24} color={themeColors.primary} />
              Tips for Success
            </div>
            <ul style={listStyle}>
              <li><strong>List consistently</strong> — Sellers who add new items regularly rank higher in search results</li>
              <li><strong>Respond quickly</strong> — Fast response times build buyer confidence</li>
              <li><strong>Build your reputation</strong> — Aim for 5-star reviews by being honest, communicative, and reliable</li>
              <li><strong>Cross-promote</strong> — Share your BabyResell listings on social media</li>
              <li><strong>Seasonal strategy</strong> — List seasonal items 4-6 weeks before the season starts</li>
              <li><strong>Relist stale items</strong> — If an item hasn't sold in 30 days, update the photos or lower the price</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerGuide;
